import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Icon, Segmented, useToast } from "@agentsaas/ui";
import { ApiError, type Approval } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatTime } from "../app/format";
import { qk } from "../data/queries";

type Remember = "once" | "session" | "always";

function inputSummary(input: unknown): string {
  if (input && typeof input === "object") {
    const o = input as Record<string, unknown>;
    const v = o.command ?? o.file_path ?? o.path ?? o.url;
    if (typeof v === "string") return v;
  }
  return JSON.stringify(input, null, 2);
}

/** 审批卡：允许 / 拒绝，可选"本次 / 本会话 / 总是" */
export function ApprovalCard({ approval, agentName, compact }: { approval: Approval; agentName: string; compact?: boolean }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const { api } = useServices();
  const qc = useQueryClient();
  const tz = useSession().state.user?.timezone;
  const [remember, setRemember] = useState<Remember>("once");
  const [busy, setBusy] = useState<"allow" | "deny" | null>(null);

  const decide = async (decision: "allow" | "deny") => {
    setBusy(decision);
    try {
      await api.call("approvalDecide", { params: { apid: approval.id }, body: { decision, remember } });
      toast.show({ message: decision === "allow" ? t("approval.allowed") : t("approval.denied"), tone: decision === "allow" ? "success" : "neutral", duration: 2000 });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      if (!(e instanceof ApiError && e.code === "approval.already_resolved")) {
        setBusy(null);
        return;
      }
    }
    qc.setQueryData<Approval[]>(qk.approvals(approval.projectId), (xs) => xs?.filter((x) => x.id !== approval.id));
    setBusy(null);
  };

  return (
    <article className={`approval${compact ? " approval--compact" : ""}`} aria-label={t("approval.title", { agent: agentName, tool: approval.tool })} data-testid="approval-card">
      <header className="approval__head">
        <Icon name="shield" />
        <strong className="grow">{t("approval.title", { agent: agentName, tool: approval.tool })}</strong>
        <Badge tone="warning">
          <time dateTime={approval.createdAt}>{formatTime(approval.createdAt, locale, tz)}</time>
        </Badge>
      </header>
      <pre className="approval__input">{inputSummary(approval.input)}</pre>
      {approval.reason ? <p className="small muted approval__reason">{t("approval.reason", { reason: approval.reason })}</p> : null}
      <div className="approval__remember">
        <span className="small muted">{t("approval.remember")}</span>
        <Segmented<Remember>
          label={t("approval.remember")}
          value={remember}
          onChange={setRemember}
          options={[
            { value: "once", label: t("approval.remember.once") },
            { value: "session", label: t("approval.remember.session") },
            { value: "always", label: t("approval.remember.always") },
          ]}
        />
      </div>
      <div className="approval__actions">
        <Button variant="secondary" loading={busy === "deny"} disabled={!!busy} onClick={() => void decide("deny")} data-testid="approval-deny">
          {t("approval.deny")}
        </Button>
        <Button variant="primary" loading={busy === "allow"} disabled={!!busy} onClick={() => void decide("allow")} data-testid="approval-allow">
          {t("approval.allow")}
        </Button>
      </div>
    </article>
  );
}
